/**
 * smart-insights-card.tsx — AI-067 client-facing rule-based insights
 * (R2.1 sub-step 5).
 *
 * Renders 0..2 positive-framing insights inside the client `/dashboard`.
 * Strings come pre-localised from the API (the server reads the client's
 * `language` and picks the right copy). The component itself is purely
 * presentational.
 */

import { useTranslation } from 'react-i18next';

import type { SmartInsight } from '@/lib/api-client';

interface Props {
  insights: SmartInsight[];
}

const ICON_FOR: Record<SmartInsight['kind'], string> = {
  bookend_correlation: '↗',
  streak_motivation: '✦',
  weekly_summary: '★',
};

export function SmartInsightsCard({ insights }: Props): JSX.Element | null {
  const { t } = useTranslation();
  if (insights.length === 0) return null;
  return (
    <section
      data-testid="smart-insights-card"
      className="rounded-2xl border border-bone-3 bg-bone p-5 shadow-sm"
    >
      <header className="mb-3">
        <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-ink/60">
          {t('dashboard.smartInsights.eyebrow')}
        </p>
        <h2 className="font-display text-lg font-semibold text-ink">
          {t('dashboard.smartInsights.title')}
        </h2>
      </header>
      <ul className="space-y-3">
        {insights.map((insight, idx) => (
          <li
            key={`${insight.kind}-${idx}`}
            className="flex items-start gap-3 rounded-xl bg-bone-2 p-3"
            data-testid={`smart-insight-${insight.kind}`}
          >
            <span
              aria-hidden="true"
              className="font-display text-xl leading-none text-[hsl(var(--flame,28_100%_50%))]"
            >
              {ICON_FOR[insight.kind]}
            </span>
            <div>
              <p className="text-sm font-medium text-ink">{insight.title}</p>
              <p className="mt-1 text-sm text-ink/80">{insight.body}</p>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
